import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { sendContact } from "../../lib/services";
import { track } from "../../lib/analytics";

const inputCls =
  "w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-4 py-3 text-sm text-[var(--ink)] placeholder:text-[var(--muted)]/60 focus:border-[var(--cyan)]/60 focus:outline-none transition-colors";

const Field = ({ label, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="mono block text-[11px] tracking-[0.2em] uppercase text-[var(--muted)] mb-2">
      {label}
    </label>
    {children}
  </div>
);

const newChallenge = () => {
  const a = 2 + Math.floor(Math.random() * 7);
  const b = 2 + Math.floor(Math.random() * 7);
  return { a, b };
};

export const ContactForm = () => {
  const [form, setForm] = useState({ nombre: "", empresa: "", email: "", telefono: "", necesidad: "", presupuesto: "", inicio: "", website: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [challenge, setChallenge] = useState(newChallenge);
  const [captchaInput, setCaptchaInput] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    if (parseInt(captchaInput, 10) !== challenge.a + challenge.b) {
      setStatus("error");
      setErrorMsg("La prueba anti-robot no es correcta. Inténtalo de nuevo.");
      setChallenge(newChallenge());
      setCaptchaInput("");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await sendContact(form);
      if (res.status === "ok") {
        setStatus("success");
        track("contact_form_submit");
      } else if (res.status === "activation") {
        setStatus("error");
        setErrorMsg("El buzón está en activación inicial (proceso único de un minuto). Escríbenos mientras tanto a netsys.pma@gmail.com o agenda una reunión.");
      } else {
        throw new Error(res.detail || "Error");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg("No pudimos enviar tu solicitud. Inténtalo de nuevo en unos segundos.");
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-2xl border border-[var(--green)]/40 bg-[var(--surface)] p-10 text-center"
        data-testid="contact-success"
        role="status"
      >
        <CheckCircle2 size={44} className="mx-auto text-[var(--green)] mb-4" />
        <h3 className="text-2xl font-bold mb-2">Solicitud recibida</h3>
        <p className="text-[var(--muted)] text-sm max-w-sm mx-auto leading-relaxed">
          Gracias, {form.nombre.split(" ")[0]}. Un especialista de Netsys revisará tu mensaje y te contactará para conversar sobre tu proyecto.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-[var(--line)] bg-[var(--surface)]/60 p-6 sm:p-8 space-y-5" data-testid="contact-form" noValidate={false}>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Nombre *" htmlFor="cf-nombre">
          <input id="cf-nombre" required minLength={2} className={inputCls} value={form.nombre} onChange={set("nombre")} placeholder="Tu nombre" data-testid="contact-nombre" />
        </Field>
        <Field label="Empresa" htmlFor="cf-empresa">
          <input id="cf-empresa" className={inputCls} value={form.empresa} onChange={set("empresa")} placeholder="Nombre de tu empresa" data-testid="contact-empresa" />
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Correo *" htmlFor="cf-email">
          <input id="cf-email" type="email" required className={inputCls} value={form.email} onChange={set("email")} placeholder="nombre@empresa.com" data-testid="contact-email" />
        </Field>
        <Field label="Teléfono" htmlFor="cf-tel">
          <input id="cf-tel" type="tel" className={inputCls} value={form.telefono} onChange={set("telefono")} placeholder="+507 ..." data-testid="contact-telefono" />
        </Field>
      </div>
      <Field label="¿Qué necesitas transformar? *" htmlFor="cf-necesidad">
        <textarea id="cf-necesidad" required minLength={10} rows={4} className={inputCls} value={form.necesidad} onChange={set("necesidad")} placeholder="Cuéntanos qué proceso, aplicación o reto tecnológico quieres mejorar." data-testid="contact-necesidad" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Presupuesto aproximado" htmlFor="cf-presupuesto">
          <select id="cf-presupuesto" className={inputCls} value={form.presupuesto} onChange={set("presupuesto")} data-testid="contact-presupuesto">
            <option value="">Seleccionar</option>
            <option>Menos de $5,000</option>
            <option>$5,000 – $15,000</option>
            <option>$15,000 – $50,000</option>
            <option>Más de $50,000</option>
            <option>Por definir</option>
          </select>
        </Field>
        <Field label="¿Cuándo te gustaría comenzar?" htmlFor="cf-inicio">
          <select id="cf-inicio" className={inputCls} value={form.inicio} onChange={set("inicio")} data-testid="contact-inicio">
            <option value="">Seleccionar</option>
            <option>Lo antes posible</option>
            <option>En 1–3 meses</option>
            <option>En 3–6 meses</option>
            <option>Solo estoy explorando</option>
          </select>
        </Field>
      </div>

      <Field label="Prueba anti-robot *" htmlFor="cf-captcha">
        <div className="flex items-center gap-4">
          <span className="mono shrink-0 rounded-xl border border-[var(--green)]/40 bg-[var(--green)]/10 px-4 py-3 text-sm text-[var(--green)]" data-testid="contact-captcha-question">
            ¿Cuánto es {challenge.a} + {challenge.b}?
          </span>
          <input
            id="cf-captcha"
            required
            inputMode="numeric"
            className={inputCls}
            value={captchaInput}
            onChange={(e) => setCaptchaInput(e.target.value)}
            placeholder="Tu respuesta"
            data-testid="contact-captcha-input"
          />
        </div>
      </Field>

      <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      {status === "error" && (
        <p className="text-sm text-red-400" role="alert" data-testid="contact-error">{errorMsg}</p>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        className="btn-glow inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[var(--surface2)] px-8 py-3.5 text-sm font-semibold disabled:opacity-60"
        data-testid="contact-submit-button"
        data-cursor="cta"
      >
        {status === "loading" ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Enviando...
          </>
        ) : (
          <>
            Enviar solicitud <Send size={15} className="text-[var(--green)]" />
          </>
        )}
      </button>
      <p className="mono text-[11px] text-[var(--muted)]/60">Tu solicitud llega directo a nuestro correo. Sin intermediarios.</p>
    </form>
  );
};
